<template>
  <div
    v-if="drawer"
    class="tw-flex tw-items-center tw-gap-2"
    data-testid="graph-drawer-controls"
  >
    <!--
      Styled as the secondary button next to it so the two controls match;
      the label stays a label so the click reaches the toggle's checkbox.
      data-pinned is the saved state, data-pending-pinned what the next save
      sends.
    -->
    <label
      class="sf-button sf-button--secondary sf-button--lg tw-m-0 !tw-gap-1.5"
      :class="{ '!tw-cursor-default': drawer.saving }"
      data-testid="graph-drawer-main-chart"
      :data-pinned="String(drawer.pinned)"
      :data-pending-pinned="String(drawer.pendingPinned)"
    >
      <SfToggle
        :model-value="drawer.pendingPinned"
        :icons="false"
        :disabled="drawer.saving"
        :aria-label="mainChartLabel"
        @update:model-value="emit('setPinned', $event)"
      />
      {{ mainChartLabel }}
    </label>
    <!-- A disabled button gets no mouse events, so the tooltip sits on the wrapper. -->
    <span
      v-tooltip="pinMetricTooltip"
      class="tw-inline-block"
      data-testid="graph-drawer-pin-metric-wrapper"
      :data-highlighted="String(drawer.highlighted)"
    >
      <SfButtonDs
        variant="secondary"
        icon-before="thumbtack"
        :disabled="pinMetricDisabled"
        data-testid="graph-drawer-pin-metric"
        @click="emit('setHighlighted', !drawer.pendingHighlighted)"
      >
        {{ pinMetricLabel }}
      </SfButtonDs>
    </span>

    <SfModal
      v-if="drawer.confirmingReplace"
      size="xs"
      data-testid="graph-drawer-replace-main-chart"
      @close="emit('cancelReplace')"
    >
      <template #title>{{
        I18n.t('report.drawer.editor.replace_main_chart.title')
      }}</template>
      <template #body>
        <p class="tw-m-0 tw-text-center">
          {{ I18n.t('report.drawer.editor.replace_main_chart.body_first') }}
          <br />
          {{ I18n.t('report.drawer.editor.replace_main_chart.body_second') }}
        </p>
      </template>
      <template #footer>
        <SfButtonDs
          variant="secondary"
          data-testid="graph-drawer-replace-main-chart-cancel"
          @click="emit('cancelReplace')"
        >
          {{ I18n.t('report.drawer.editor.replace_main_chart.cancel') }}
        </SfButtonDs>
        <SfButtonDs
          variant="primary"
          data-testid="graph-drawer-replace-main-chart-confirm"
          @click="emit('confirmReplace')"
        >
          {{ I18n.t('report.drawer.editor.replace_main_chart.confirm') }}
        </SfButtonDs>
      </template>
    </SfModal>
  </div>
</template>

<script setup lang="ts">
import { computed } from 'vue'

import SfButtonDs from '@app/shared/vue/components/sf-button/sf-button-ds.vue'
import { SfModal } from '@app/shared/vue/components/sf-modal'
import SfToggle from '@app/shared/vue/components/sf-toggle.vue'
import useI18n from '@app/shared/vue/composables/use-I18n'

defineOptions({
  name: 'GraphDrawerControls',
})

export interface DrawerSetup {
  pinned: boolean
  highlighted: boolean
  pendingPinned: boolean
  pendingHighlighted: boolean
  confirmingReplace: boolean
  highlightedCount: number
  highlightedLimit: number
  otherMainChart: boolean
  saving: boolean
}

interface Props {
  drawer?: DrawerSetup | null
}

const props = withDefaults(defineProps<Props>(), {
  drawer: null,
})

const emit =
  defineEmits<{
    setPinned: [boolean]
    setHighlighted: [boolean]
    confirmReplace: []
    cancelReplace: []
  }>()

const I18n = useI18n()

const mainChartLabel = computed(() => I18n.t('report.drawer.editor.main_chart'))

const pinMetricLabel = computed(() =>
  props.drawer?.pendingHighlighted
    ? I18n.t('report.drawer.editor.unpin_metric')
    : I18n.t('report.drawer.editor.pin_metric')
)

// The saved count includes this graph when it is highlighted, so it does not
// count against itself when the user unpins and pins again before saving.
const capReached = computed(() => {
  const setup = props.drawer
  if (!setup || setup.pendingHighlighted) return false
  const others = setup.highlightedCount - (setup.highlighted ? 1 : 0)
  return others >= setup.highlightedLimit
})

const pinMetricDisabled = computed(
  () => !!props.drawer?.saving || capReached.value
)

const pinMetricTooltip = computed(() =>
  capReached.value
    ? I18n.t('report.drawer.highlighted.cap_reached_tooltip')
    : ''
)
</script>
